<template>
  <el-dialog
    title="Add partner"
    :visible.sync="isVisible"
    :close-on-click-modal="false"
    width="480px"
    @close="resetForm"
  >
    <div class="add-partner-modal">
      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          Partner name
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-input
          v-model="form.partner_name"
          placeholder="Partner agency name"
        />
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          Email
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-input
          v-model="form.email"
          placeholder="owner@example.com"
        />
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          Phone
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-input
          v-model="form.phone"
          placeholder="+380501112244"
          @blur="form.phone = normalizePhone(form.phone)"
        />
        <span v-if="phoneError" class="add-partner-modal__error">
          {{ phoneError }}
        </span>
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          First name
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-input
          v-model="form.first_name"
          placeholder="First name"
        />
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          Last name
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-input
          v-model="form.last_name"
          placeholder="Last name"
        />
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">
          Country
          <span class="add-partner-modal__required">*</span>
        </span>
        <el-select
          v-model="form.country_code"
          class="add-partner-modal__select"
          placeholder="Select country"
          no-data-text="No data"
          no-match-text="No match"
          filterable
          clearable
        >
          <el-option
            v-for="country in countries"
            :key="country.code"
            :label="country.name.en"
            :value="country.code"
          />
        </el-select>
      </label>

      <label class="add-partner-modal__field">
        <span class="add-partner-modal__label">Clinic ID</span>
        <el-input
          v-model="form.clinic_id"
          placeholder="Clinic ID (optional)"
        />
        <span v-if="clinicIdError" class="add-partner-modal__error">
          {{ clinicIdError }}
        </span>
      </label>
    </div>

    <div slot="footer" class="add-partner-modal__footer">
      <base-button type="default" @click="cancel">Cancel</base-button>
      <base-button
        v-if="canManageAgencies"
        type="primary"
        :disabled="!isValid || isSubmitting"
        @click="submit"
      >
        {{ isSubmitting ? 'Submitting...' : 'Submit' }}
      </base-button>
    </div>
  </el-dialog>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import { mapState, mapActions } from 'vuex';

const buildForm = () => ({
  partner_name: '',
  email: '',
  phone: '',
  first_name: '',
  last_name: '',
  country_code: '',
  clinic_id: '',
});

  export default {
  mixins: [agencyAccess],
    props: {
      show: {
        type: Boolean,
        default: false,
      },
      clinicId: {
        type: [String, Number],
        default: '',
      },
    },
    data() {
      return {
        form: buildForm(),
        isSubmitting: false,
      };
    },
    computed: {
      ...mapState('cmsApiCatalogs', ['countries']),
      isVisible: {
        get() {
          return this.show;
        },
        set(value) {
          this.$emit('update:show', value);
        },
      },
      phoneError() {
        if (!this.form.phone) {
          return '';
        }
        // valid once normalization yields '+' followed by at least 7 digits
        return /^\d{7,15}$/.test(this.normalizePhone(this.form.phone).slice(1))
          ? ''
          : 'Enter a valid phone number';
      },
      clinicIdError() {
        const value = String(this.form.clinic_id).trim();
        if (!value) {
          return '';
        }
        return /^\d+$/.test(value) ? '' : 'Clinic ID must be a number';
      },
      isValid() {
        return Boolean(
          this.form.partner_name
          && this.form.email
          && this.form.phone
          && this.form.first_name
          && this.form.last_name
          && this.form.country_code,
        ) && !this.phoneError && !this.clinicIdError;
      },
    },
    watch: {
      // Prefill Clinic ID with the current clinic when the modal opens
      show(value) {
        if (value) {
          this.form.clinic_id = this.clinicId != null ? String(this.clinicId) : '';
        }
      },
    },
    created() {
      if (!this.countries.length) {
        this.getCountries({ perPage: 1500 });
      }
    },
    methods: {
      ...mapActions('cmsApiCatalogs', ['getCountries']),
      ...mapActions('agencies', ['createAgency']),
      cancel() {
        this.isVisible = false;
      },
      // Strip everything but digits and force a single leading '+'
      // e.g. "+38(063)715-79-66" -> "+380637157966"
      normalizePhone(value) {
        const digits = String(value || '').replace(/\D/g, '');
        return digits ? `+${digits}` : '';
      },
      buildPayload() {
        const payload = {
          email: this.form.email,
          phone: this.normalizePhone(this.form.phone),
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          partner_name: this.form.partner_name,
          country_code: this.form.country_code,
        };

        const clinicId = String(this.form.clinic_id).trim();
        if (clinicId && !this.clinicIdError) {
          payload.clinic_id = Number(clinicId);
        }

        return payload;
      },
      async submit() {
        if (!this.canManageAgencies) return;
        if (!this.isValid || this.isSubmitting) {
          return;
        }
        this.isSubmitting = true;

        try {
          const response = await this.createAgency(this.buildPayload());
          this.$emit('submit', response.data);
          this.$notify({ type: 'success', title: 'Partner created' });
          this.isVisible = false;
        } catch (error) {
          const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Failed to create partner';
          this.$notify({ type: 'warning', title: message });
        } finally {
          this.isSubmitting = false;
        }
      },
      resetForm() {
        this.form = buildForm();
        this.isSubmitting = false;
      },
    },
  }
</script>

<style lang="scss" scoped>
  .add-partner-modal {
    display: flex;
    flex-direction: column;
    gap: 14px;
    &__field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    &__label {
      font-size: 13px;
      color: #555;
    }
    &__required {
      color: #ec250d;
    }
    &__error {
      font-size: 12px;
      color: #ec250d;
    }
    &__select {
      width: 100%;
    }
    &__footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
  }
</style>